import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
  AppStatus,
  DynamicForm,
  DynamicFormValuesSections,
  GetQbAppResponse,
  PrequalAppScoring,
  SpecialValueAccessor,
} from '@app/models';
import { AppReferenceReports } from '@src/app/LAUSD/models/lausd-reference-report.model';
import { TransformingFormArray } from '@src/assets/helpers/transformingFormGroup';
import { BehaviorSubject, map, ReplaySubject, Subject, takeWhile } from 'rxjs';
import { LoginService } from './login.service';

/**
 * Service for storing all data about the active application.
 *
 * We use this to store things like AppStatus, User Type, App Type, Comments Read/Write/Hidden, and and other random data like CSLB.
 */
@Injectable({
  providedIn: 'root',
})
export class AppInfoService {
  /**
   * =======================
   * App Information - properties of the app itself.
   * =======================
   */

  /** Id of the application. */
  id: number = 0;

  private _isFullServiceApp: boolean;
  get isFSApp() {
    return this._isFullServiceApp;
  }
  get isSOApp() {
    return !this._isFullServiceApp;
  }

  status: AppStatus;

  private _parsedQuestionnaire: DynamicForm;
  private _questionnaireTypeId: number;
  get questionnaireTypeId() {
    return this._questionnaireTypeId;
  }
  set questionnaireTypeId(id) {
    this._questionnaireTypeId = id;
  }
  get isPrequal() {
    return this._questionnaireTypeId == 1;
  }
  get isCUPCCAA() {
    return this._questionnaireTypeId == 2;
  }
  get isSafety() {
    return this._questionnaireTypeId == 3;
  }

  get userIsVendor() {
    return this.loginService.isVendor;
  }
  get userIsAgency() {
    return this.loginService.isAgency;
  }
  get userIsFSAgency() {
    return this.loginService.user.isFullService && this.loginService.isAgency;
  }
  get userIsSOAgency() {
    return !this.loginService.user.isFullService && this.loginService.isAgency;
  }
  get userIsQbat() {
    return this.loginService.isQbat;
  }
  displaySOVerifyBoxes = false;
  soVerifyBoxesRequiredForValidation = false;

  /** Steps where Next/Save failed — used for red step header after a proceed attempt. */
  vendorStepValidationAttempted = new Set<number>();
  /** While validateForm() runs (or after it fails) so conditional required fields can validate. */
  validatingStepIndex: number | null = null;
  validatingAllSteps = false;

  isStepValidating(stepIndex: number | undefined): boolean {
    if (this.validatingAllSteps) {
      return true;
    }
    if (stepIndex == null) {
      return false;
    }
    return this.validatingStepIndex === stepIndex;
  }

  shouldShowFieldValidationErrors(stepIndex: number | undefined): boolean {
    if (stepIndex == null) {
      return false;
    }
    return this.vendorStepValidationAttempted.has(stepIndex) || this.isStepValidating(stepIndex);
  }

  clearStepValidationState(stepIndex: number): void {
    this.vendorStepValidationAttempted.delete(stepIndex);
    if (this.validatingStepIndex === stepIndex) {
      this.validatingStepIndex = null;
    }
    this.validatingAllSteps = false;
    this.appForm?.get([stepIndex])?.markAsUntouched();
  }

  resetStepValidationState(): void {
    this.validatingStepIndex = null;
    this.validatingAllSteps = false;
  }

  clearAllStepValidationState(): void {
    this.vendorStepValidationAttempted.clear();
    this.resetStepValidationState();
  }

  _soAgencyCanRate = new BehaviorSubject<boolean>(true);
  get soAgencyCanRate() {
    return this._soAgencyCanRate.asObservable().pipe(map((val: boolean) => (!this.isSOApp ? true : val)));
  }
  setSOAgencyCanRate(val) {
    this._soAgencyCanRate.next(val);
  }

  /**
   * =======================
   * User-provided Data - information extracted from the filled-out application.
   * =======================
   */

  /** The top-level FormArray containing all the steps with application form data. */
  appForm: TransformingFormArray;
  appSectionFormGroups: { [sectionName: string]: UntypedFormGroup };

  /** CSLB number - will need to be referenced in multiple places throughout the app. */
  cslbNumber: number;

  /** Small Business - will be referenced by a FS agency when calculating the MaxProposedRate */
  smallBusiness: boolean;

  private _rateFinalization: number;
  /** Used on a rated prequal app to display in the header of the app.  */
  get rateFinalization() {
    return this._rateFinalization;
  }
  set rateFinalization(r) {
    if (this.status.APPROVED) {
      this._rateFinalization = r;
    }
  }

  private _rateNotApplicable: boolean;
  get rateNotApplicable() {
    return this._rateNotApplicable;
  }

  private _proposedRate: number;
  get proposedRate() {
    return this._proposedRate;
  }

  /**
   * =======================
   * LAUSD Reference Reports
   * =======================
   * */
  referenceReports: AppReferenceReports;

  /**
   * =======================
   * Services for triggering data refresh events.
   * =======================
   */

  /** Trigger a refresh of the event log. */
  triggerRefreshEventLog = new Subject<boolean>();

  private _customValueSubjects = new Map<string, ReplaySubject<any>>();
  private _customValueFormControls = new Map<string, AbstractControl>();

  constructor(private loginService: LoginService) {}

  setAppInfo(app: GetQbAppResponse) {
    this.id = app.id;
    this.status = new AppStatus(app.status);
    this._isFullServiceApp = app.fullServiceApp;
    this.questionnaireTypeId = app.questionnaireTypeId;
    this._parsedQuestionnaire = JSON.parse(app.applicationBody) as DynamicForm;
    this.rateFinalization = app.rateFinalization;
    this._rateNotApplicable = app.rateNotApplicable;
    this._proposedRate = app.proposedRate;
    this.displaySOVerifyBoxes = app.displaySOVerifyBoxes;
    this.soVerifyBoxesRequiredForValidation = app.soVerifyBoxesRequiredForValidation;
    this.referenceReports = app.referenceReportFiles;
    this.applySOAgencyVerifyPolicy();
  }

  /**
   * Software-only agency users never use per-step Verify UI; list/rate must not depend on verification.
   * API flags may differ by agency (e.g. legacy LA); product rules are enforced here for Prequal and CUPCCAA.
   */
  applySOAgencyVerifyPolicy(): void {
    if (!this.userIsSOAgency) return;
    this.displaySOVerifyBoxes = false;
    this.soVerifyBoxesRequiredForValidation = false;
    this.setSOAgencyCanRate(true);
  }

  resetApplication() {
    this.id = 0;
    this._isFullServiceApp = undefined;
    this.status = undefined;
    this.cslbNumber = undefined;
    this._rateFinalization = undefined;
    this._rateNotApplicable = undefined;
    this._proposedRate = undefined;
    this._customValueSubjects = new Map<string, ReplaySubject<any>>();
    this._customValueFormControls = new Map<string, AbstractControl>();
    this.appForm = undefined;
    this.appSectionFormGroups = undefined;
    this.referenceReports = undefined;
    this.displaySOVerifyBoxes = false;
    this.soVerifyBoxesRequiredForValidation = false;
    this.clearAllStepValidationState();
    this.setSOAgencyCanRate(true);
  }

  getSmallBusinessValue(appFormSections: DynamicFormValuesSections) {
    const smBusControlNames = this._parsedQuestionnaire.specialValues?.smallBusiness;
    this.smallBusiness =
      appFormSections[smBusControlNames?.sectionName]?.get(smBusControlNames?.controlName)?.value ?? false;
  }

  setAppScores(appScores: PrequalAppScoring[]) {
    if (!Array.isArray(appScores)) {
      return;
    }

    const score6D = appScores.find(scoreObj => scoreObj.sectionId == 'step-6-d');
    if (score6D) {
      this.setCustomValue('score6D', score6D.actual);
    }

    const score4 = appScores.find(scoreObj => scoreObj.sectionId == 'step-4');
    if (score4) {
      this.setCustomValue('score4', score4.actual);
    }
  }

  /**
   * If we need to get a value from anywhere in the app form, we can use this method.
   * Pass in the key of the special value accessor (as listed at the end of the prequal questionnaire)
   * and it will return a ReplaySubject that will emit the value whenever it changes.
   *
   * If you try to access the same value in multiple places, it will return the first created replaySubject instead of creating a new one.
   */
  getCustomValueSubject(key: string): ReplaySubject<any> {
    if (this._customValueSubjects.has(key)) {
      return this._customValueSubjects.get(key);
    }

    const accessor: SpecialValueAccessor = this._parsedQuestionnaire.specialValues?.[key];
    if (!accessor) {
      throw new Error(`Special value accessor not found for key: ${key}`);
    }

    const s = new ReplaySubject<any>(1);
    const currentId = this.id;
    const formControl = this.getSpecialValueFormControl(key);
    if (formControl) {
      // Emit the current value immediately
      s.next(formControl.value);

      // Only vendors can update the form so this is only necessary for vendors.
      if (this.userIsVendor) {
        formControl.valueChanges.pipe(takeWhile(() => this.id === currentId)).subscribe({
          next: value => s.next(value),
          complete: () => s.complete(),
        });
      }
    } else {
      throw new Error(`Form Control not found for special value accessor: ${key} - ${accessor.controlName}`);
    }

    this._customValueSubjects.set(key, s);
    return s;
  }

  /** Accepts a key and a value, to be set on the formcontrol of the corresponding key. Mainly useful for setting things like helpers.*/
  setCustomValue(key: string, value, emitEvent = true) {
    const formControl = this.getSpecialValueFormControl(key);
    if (formControl) {
      if (formControl.value !== value) {
        formControl.setValue(value, { emitEvent: emitEvent });
      }
    } else {
      console.warn('Custom value formControl not found: ' + key);
    }
  }

  private getSpecialValueFormControl(key: string): AbstractControl {
    if (this._customValueFormControls.get(key)) {
      return this._customValueFormControls.get(key);
    }

    const accessor: SpecialValueAccessor = this._parsedQuestionnaire.specialValues?.[key];
    if (!accessor) {
      throw new Error(`Special value accessor not found for key: ${key}`);
    }

    // const step = this.appForm.controls.find((control: UntypedFormGroup) => {
    //   return Object.keys(control.controls).includes(accessor.sectionName);
    // });

    const section = this.appSectionFormGroups?.[accessor.sectionName];

    const formControl = section?.get(accessor.controlName);

    if (formControl) this._customValueFormControls.set(key, formControl);

    return formControl;
  }
}
